import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Users, Check } from "lucide-react";
import api from "../lib/api";
import { imgUrl } from "../components/ExperienceCard";
import SEO, { breadcrumbLD, SITE_URL } from "../components/SEO";
import GoogleRatingBadge from "../components/GoogleRatingBadge";

export default function ExperienceDetail() {
    const { slug } = useParams();
    const [xp, setXp] = useState(null);
    const [error, setError] = useState("");
    const [lead, setLead] = useState({ full_name: "", email: "", message: "" });
    const [leadStatus, setLeadStatus] = useState("idle");

    useEffect(() => {
        setXp(null); setError("");
        api.get(`/experiences/${slug}`).then((r) => setXp(r.data)).catch(() => setError("Expérience introuvable."));
    }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        setLeadStatus("loading");
        try {
            await api.post("/booking-leads", {
                ...lead,
                message: `[${xp.title}] ${lead.message}`,
            });
            setLeadStatus("success");
        } catch { setLeadStatus("idle"); }
    };

    if (error) return <div className="pt-40 text-center pb-40"><h1 className="cp-serif text-5xl">{error}</h1><Link to="/experiences" className="cp-btn-ghost mt-10 inline-flex">Retour</Link></div>;
    if (!xp) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;

    const xpLD = {
        "@context": "https://schema.org",
        "@type": "TouristAttraction",
        "name": xp.title,
        "description": (xp.description || xp.tagline || "").slice(0, 320),
        "image": xp.image ? (xp.image.startsWith("http") ? xp.image : `${SITE_URL}${xp.image}`) : undefined,
        "address": {
            "@type": "PostalAddress",
            "addressRegion": "Normandie / Picardie",
            "addressCountry": "FR",
            "addressLocality": xp.city_slug || "",
        },
        ...(xp.price_from ? {
            "offers": {
                "@type": "Offer",
                "price": String(xp.price_from).replace(/[^0-9]/g, "") || "0",
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock",
            },
        } : {}),
    };

    return (
        <div data-testid={`xp-detail-${slug}`}>
            <SEO
                title={xp.title}
                description={xp.tagline || xp.short_description || `${xp.title} — expérience vérifiée Côte Perdue.`}
                image={xp.image}
                path={`/experiences/${slug}`}
                type="article"
                keywords={[xp.title, xp.category, "Normandie", "Picardie", "expérience", ...(xp.mood_tags || []), ...(xp.tags || [])]}
                structuredData={[
                    xpLD,
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Expériences", path: "/experiences" },
                        { name: xp.title, path: `/experiences/${slug}` },
                    ]),
                ]}
            />
            <section className="relative min-h-[80vh] overflow-hidden">
                <img src={imgUrl(xp.image)} alt={xp.title} className="absolute inset-0 w-full h-full object-cover cp-kenburns cp-photo-real-dark" />
                <div className="absolute inset-0 cp-hero-overlay" />
                {/* Photo credit — Wikimedia real or editorial dream composition */}
                {xp.image && (xp.image.includes('/static/img/real/') || xp.image.includes('/static/img/dream/')) && (
                    <span className="absolute top-24 right-5 md:right-12 z-20 cp-tiny text-[10px] text-cp-ivoire/80 bg-black/25 backdrop-blur px-2 py-1 uppercase tracking-widest" data-testid="xp-photo-credit">
                        {(xp.image.includes('/dream/') || /\/real\/(phare_brighton|jardins_le_sidaner|chateau_arques)\.jpg/.test(xp.image)) ? 'Composition éditoriale' : 'Photo · Wikimedia Commons'}
                    </span>
                )}
                <div className="relative z-10 flex flex-col justify-end min-h-[80vh] px-6 md:px-12 py-20 max-w-[1600px] mx-auto">
                    <Link to="/experiences" className="cp-tiny text-cp-ivoire/70 hover:text-cp-ivoire flex items-center gap-2 mb-6">
                        <ArrowLeft size={14} strokeWidth={1.25} /> Expériences
                    </Link>
                    <p className="cp-tiny text-cp-sable mb-4">N°{String(xp.position).padStart(2, "0")} · {xp.category}</p>
                    <h1 className="cp-serif text-cp-ivoire text-3xl sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[72px] leading-[1.05] max-w-4xl tracking-[-0.02em]">
                        {xp.title}
                    </h1>
                    <p className="cp-serif italic text-cp-ivoire/90 text-xl md:text-2xl font-light leading-snug mt-8 max-w-3xl">
                        {xp.tagline}
                    </p>
                    {xp.google_rating && (
                        <div className="mt-6">
                            <GoogleRatingBadge
                                rating={xp.google_rating}
                                count={xp.google_review_count}
                                placeId={xp.google_place_id}
                                variant="dark"
                            />
                        </div>
                    )}
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
                <aside className="md:col-span-4 flex flex-col gap-8">
                    <div>
                        <p className="cp-tiny text-cp-foret mb-2">Durée</p>
                        <p className="flex items-center gap-2 text-cp-encre text-xl"><Clock size={16} strokeWidth={1.25} /> {xp.duration}</p>
                    </div>
                    <div>
                        <p className="cp-tiny text-cp-foret mb-2">Groupe</p>
                        <p className="flex items-center gap-2 text-cp-encre text-xl"><Users size={16} strokeWidth={1.25} /> {xp.group_size}</p>
                    </div>
                    {xp.price_from && (
                        <div>
                            <p className="cp-tiny text-cp-foret mb-2">À partir de</p>
                            <p className="cp-serif text-3xl">{xp.price_from}</p>
                        </div>
                    )}
                    {xp.included?.length > 0 && (
                        <div>
                            <p className="cp-tiny text-cp-foret mb-4">Inclus</p>
                            <ul className="flex flex-col gap-2.5">
                                {xp.included.map((it, i) => (
                                    <li key={i} className="flex items-start gap-2 text-cp-encre text-sm">
                                        <Check size={14} strokeWidth={1.5} className="mt-1 text-cp-foret" /> {it}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>
                <div className="md:col-span-8">
                    <p className="text-cp-encre text-xl md:text-2xl font-light leading-relaxed whitespace-pre-line">{xp.description}</p>

                    {xp.highlights?.length > 0 && (
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {xp.highlights.map((h, i) => (
                                <div key={i} className="border-t border-cp-encre pt-4">
                                    <p className="cp-tiny text-cp-sable-deep mb-2">Point fort · 0{i + 1}</p>
                                    <p className="cp-serif italic text-lg text-cp-encre">{h}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Verified — link to the official source */}
                    {xp.verified && xp.source_url ? (
                        <div className="mt-16 bg-cp-craie p-8 md:p-12 border border-cp-foret/30" data-testid="xp-official-source">
                            <p className="cp-tiny text-cp-foret mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-cp-foret" /> Adresse vérifiée · réservation directe
                            </p>
                            <h2 className="cp-serif text-3xl md:text-5xl leading-tight text-cp-encre mb-4">
                                Réservez <em className="cp-display-italic text-cp-foret">en direct</em> auprès de l'organisateur officiel.
                            </h2>
                            {xp.official_name && (
                                <p className="cp-serif italic text-lg text-cp-gris mb-3">{xp.official_name}</p>
                            )}
                            {xp.booking_phone && (
                                <p className="cp-tiny text-cp-gris mb-6">Téléphone · <a href={`tel:${xp.booking_phone.replace(/\s+/g, '')}`} className="text-cp-foret underline-offset-4 hover:underline">{xp.booking_phone}</a></p>
                            )}
                            <a
                                href={xp.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="xp-source-link"
                                className="cp-btn-primary inline-flex items-center gap-2"
                            >
                                Accéder au site officiel ↗
                            </a>
                            <p className="cp-tiny text-cp-gris/70 mt-6 max-w-xl cp-serif italic">
                                Chez Côte Perdue, nous ne vendons rien à votre place : nous renvoyons vers la source officielle. Pas de commission cachée, pas d'intermédiaire.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-16 bg-cp-craie p-8 md:p-12 border border-cp-border" data-testid="xp-contact">
                            <p className="cp-tiny text-cp-foret mb-4">Réserver cette expérience</p>
                            <h2 className="cp-serif text-4xl md:text-5xl leading-tight text-cp-encre mb-8">
                                Écrivez-nous. <em className="cp-display-italic text-cp-foret">Nous arrangeons tout.</em>
                            </h2>
                            {leadStatus === "success" ? (
                                <p className="cp-serif italic text-2xl text-cp-foret" data-testid="xp-lead-success">Merci. Nous revenons vers vous sous 24h, toujours par mail.</p>
                            ) : (
                                <form onSubmit={submit} className="flex flex-col gap-5 max-w-xl" data-testid="xp-lead-form">
                                    <input required placeholder="Prénom Nom" value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })} className="cp-input" data-testid="xp-lead-name" />
                                    <input type="email" required placeholder="votre@adresse.email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="cp-input" data-testid="xp-lead-email" />
                                    <textarea rows={3} placeholder="Quand ? Avec qui ? Une envie particulière ?" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="cp-input" />
                                    <button type="submit" disabled={leadStatus === "loading"} className="cp-btn-primary self-start mt-4" data-testid="xp-lead-submit">
                                        Envoyer la demande
                                    </button>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
